 'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import MercadoPagoBadge from '@/components/payments/MercadoPagoBadge';
import { startTopupCheckout } from '@/lib/payments/client-checkout';

interface PricingPreviewSectionProps {
  className?: string;
}

const PricingPreviewSection = ({ className = '' }: PricingPreviewSectionProps) => {
  const router = useRouter();
  const { isAuthenticated, userProfile } = useAuth();
  const [loadingCard, setLoadingCard] = useState<string | null>(null);

  const hasLynMetPlan = userProfile?.user_plan === 'emprende';

  const goToPlan = () => {
    if (!isAuthenticated) {
      router.push('/pricing-page');
      return;
    }

    router.push('/payment/lynmet-subscription');
  };

  const goToTopup = async () => {
    if (!isAuthenticated || !hasLynMetPlan) {
      router.push('/pricing-page');
      return;
    }

    setLoadingCard('topup');
    try {
      await startTopupCheckout({ productId: 'topup_80', router });
    } catch (error) {
      console.error('Landing topup checkout error:', error);
      router.push('/pricing-page');
    } finally {
      setLoadingCard(null);
    }
  };

  const cards = [
    {
      id: 'free',
      label: 'Gratis',
      price: '$0',
      bullets: ['40 tokens al registrarte', '+10/semana si ≤5 tokens'],
      cta: 'Empezar',
      micro: 'Incluye todos los mentores',
      icon: 'SparklesIcon',
      onClick: () => {
        if (!isAuthenticated) {
          router.push('/register');
          return;
        }
        router.push('/user-dashboard');
      },
      highlight: false,
    },
    {
      id: 'lynmet',
      label: 'LynMet',
      price: '$9.900/mes',
      bullets: ['100 tokens al activar', 'Recarga mensual hasta 100 si saldo < 100', '+20/semana si ≤5 tokens'],
      cta: 'Activar plan',
      micro: 'Todo incluido + más continuidad',
      icon: 'StarIcon',
      onClick: goToPlan,
      highlight: true,
    },
    {
      id: 'topup',
      label: 'Recarga',
      price: '$2.900',
      bullets: ['2900 por recarga', 'Solo para usuarios LynMet'],
      cta: hasLynMetPlan ? 'Comprar recarga' : 'Ver recarga',
      micro: 'Para que no pares nunca',
      icon: 'BoltIcon',
      onClick: goToTopup,
      highlight: false,
    },
  ];

  return (
    <section className={`py-10 lg:py-12 bg-background ${className}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <div className="section-eyebrow mb-4">
            <Icon name="CurrencyDollarIcon" size={20} className="text-primary" />
            <span>Planes y tokens</span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground">
            Empieza gratis.{' '}
            <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'var(--gradient-app)' }}>
              Escala
            </span>{' '}
            cuando lo necesites.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card) => (
            <div
              key={card.id}
              className={`app-card relative rounded-2xl p-6 transition-smooth hover:shadow-elevation-lg ${
                card.highlight
                  ? 'border-primary shadow-elevation-md'
                  : ''
              }`}
            >
              {card.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-primary text-primary-foreground rounded-full shadow-elevation-md text-xs">
                    <Icon name="StarIcon" size={12} variant="solid" />
                    <span className="font-medium">LynMet</span>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[rgba(0,97,255,0.08)] text-primary">
                  <Icon name={card.icon as any} size={20} />
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">{card.label}</div>
                  <div className="text-2xl sm:text-3xl font-heading font-bold text-card-foreground">
                    {card.price}
                  </div>
                </div>
              </div>

              <ul className="mt-4 space-y-2 text-sm sm:text-base text-muted-foreground">
                {card.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={18} className="text-green-500 mt-0.5" />
                    <span className="line-clamp-1">{bullet}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-3 text-sm text-muted-foreground">
                {card.micro}
              </div>

              <button
                type="button"
                onClick={card.onClick}
                disabled={loadingCard === card.id}
                className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold transition-smooth ${
                  card.highlight
                    ? 'btn-primary-app'
                    : 'btn-secondary-app'
                }`}
              >
                <Icon name="ArrowRightIcon" size={16} />
                {loadingCard === card.id ? 'Abriendo checkout...' : card.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-4 text-center">
          <div className="mb-5 flex justify-center">
            <MercadoPagoBadge caption="Pagos y suscripciones con respaldo de Mercado Pago." />
          </div>
          <p className="text-sm text-muted-foreground mb-3">
            40 tokens: 1 diagnóstico potente o 3 sesiones ligeras. 80 tokens: 2 procesos completos o varias iteraciones entre mentores. 100 tokens: más continuidad mensual con diagnóstico, exploración y planes de mejora.
          </p>
          <button
            type="button"
            onClick={() => router.push('/pricing-page')}
            className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-smooth font-medium"
          >
            <span>Ver detalle de planes</span>
            <Icon name="ArrowRightIcon" size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default PricingPreviewSection;
